/**
 * MAHI onboarding — OTP resend-with-cooldown control.
 *
 * Shows a countdown ("Resend in 0:30") until `cooldownSeconds` elapses,
 * then becomes a tappable "Resend code" link. `onResend` is whatever the
 * caller needs to do to actually re-trigger the OTP send (e.g. re-POST
 * /auth/signup/start) — this component only owns the timer/disabled state.
 */
import React, { useEffect, useRef, useState } from 'react';
import { RotateCw } from 'lucide-react';

const OtpResend = ({ onResend, cooldownSeconds = 30 }) => {
  const [secondsLeft, setSecondsLeft] = useState(cooldownSeconds);
  const [busy, setBusy] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const resend = async () => {
    if (secondsLeft > 0 || busy) return;
    setBusy(true);
    try {
      await onResend();
      setSecondsLeft(cooldownSeconds);
    } finally {
      setBusy(false);
    }
  };

  if (secondsLeft > 0) {
    return (
      <p className="text-xs text-slate-400 text-center" data-testid="otp-resend-countdown">
        Resend code in 0:{String(secondsLeft).padStart(2, '0')}
      </p>
    );
  }

  return (
    <button
      type="button"
      onClick={resend}
      disabled={busy}
      data-testid="otp-resend-button"
      className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium text-raspberry-600 hover:text-raspberry-700 disabled:opacity-50"
    >
      <RotateCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />
      Resend code
    </button>
  );
};

export default OtpResend;
